// ─────────────────────────────────────────────────────────────────
// Glass-i — DGU Consumables INVENTORY page (Stage DGU-CONSUMABLE-2).
//
// The SEPARATE DGU-consumables stock view — deliberately NOT the glass Current
// Stock page. Two tabs: live Balances (per-item qty in its own unit) and the
// movement Ledger. Every figure comes from the dgu_consumable_current_stock /
// dgu_consumable_stock_ledger tables that Purchase inward-posting routes DGU
// lines into. Read-only + a manual "Add / adjust stock". Kept apart from glass.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Table, Tabs, Input, Select, Tag, Typography, Alert, Button, Space, message,
  Modal, Form, InputNumber, Radio } from 'antd';
import { ReloadOutlined, BorderOuterOutlined, PlusOutlined } from '@ant-design/icons';

import { dguConsumableStockApi } from './dguConsumableStockApi';
import { itemsApi } from '../../masters/items/itemsApi';
import { KIND_OPTIONS, kindLabel, kindColor } from '../../masters/dguConsumables/dguConsumableHelpers';
import useAuth from '../../../../hooks/useAuth';

const { Title, Text } = Typography;

const CAN_ADJUST = 'erp.inventory.dgu_consumable.adjust';
function hasPerm(me, key) {
  const list = (me && me.permissions) || [];
  return list.includes('*') || list.includes(key);
}

const NF3 = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
function fmtQty(v) { const n = Number(v); return Number.isFinite(n) ? NF3.format(n) : '—'; }
function fmtSigned(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const s = NF3.format(Math.abs(n));
  return n < 0 ? `− ${s}` : (n > 0 ? `+ ${s}` : s);
}
function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v); if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  return `${day}-${d.toLocaleString('en-US', { month: 'short' })}-${d.getFullYear()}`;
}
const errMsg = (e, fb) => e?.response?.data?.error?.message || e?.message || fb;

// Manual opening-balance / correction modal. deltaQty is signed: + adds, − reduces.
function AdjustStockModal({ open, onClose, onDone }) {
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('ADD');

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoadingItems(true);
    itemsApi.listDguConsumables({ pageSize: 200 })
      .then((res) => { if (alive) setItems(((res && res.rows) || []).map((r) => ({ value: r.itemId, label: `${r.itemName} · ${kindLabel(r.consumableKind)}` }))); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoadingItems(false); });
    return () => { alive = false; };
  }, [open]);
  useEffect(() => { if (open) { form.resetFields(); setMode('ADD'); } }, [open, form]);

  const submit = async () => {
    let vals;
    try { vals = await form.validateFields(); } catch { return; }
    const qty = Math.abs(Number(vals.qty));
    const deltaQty = mode === 'REDUCE' ? -qty : qty;
    setSaving(true);
    try {
      await dguConsumableStockApi.adjust({ itemId: vals.itemId, deltaQty, reason: vals.reason });
      message.success(`Stock ${mode === 'REDUCE' ? 'reduced' : 'added'}.`);
      onClose();
      onDone && onDone();
    } catch (e) { message.error(errMsg(e, 'Adjustment failed.')); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onCancel={onClose} onOk={submit} okText="Post adjustment" confirmLoading={saving}
      title="Adjust DGU consumable stock" destroyOnClose maskClosable={false} width={480}>
      <Alert type="info" showIcon className="mb-3"
        message="Manual stock adjustment"
        description="Add opening / found stock, or reduce for a correction. Recorded as an ADJUSTMENT in the ledger, in the item's own unit. A reduction can't take a balance below zero." />
      <Form form={form} layout="vertical" initialValues={{ qty: null }}>
        <Form.Item name="itemId" label="Consumable item" rules={[{ required: true, message: 'Pick a DGU consumable item.' }]}>
          <Select showSearch optionFilterProp="label" loading={loadingItems} options={items} placeholder="Select item" />
        </Form.Item>
        <Form.Item label="Action">
          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} optionType="button" buttonStyle="solid">
            <Radio.Button value="ADD">Add stock</Radio.Button>
            <Radio.Button value="REDUCE">Reduce</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="qty" label="Quantity (item's unit)"
          rules={[{ required: true, message: 'Enter a quantity.' }, { type: 'number', min: 0.001, message: 'Must be greater than zero.' }]}>
          <InputNumber min={0} step={1} style={{ width: '100%' }} placeholder="0" />
        </Form.Item>
        <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'A reason is required.' }]}>
          <Input.TextArea rows={2} maxLength={500} placeholder="e.g. Opening balance / stock-count correction" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function BalancesTab({ onDataChange }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const timer = useRef(null);
  const { me } = useAuth();
  const canAdjust = hasPerm(me, CAN_ADJUST);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dguConsumableStockApi.stock({ page, pageSize, search: search || undefined, consumableKind: kindFilter || undefined });
      setRows((res && res.rows) || []); setTotal((res && res.total) || 0);
    } catch (e) { message.error(errMsg(e, 'Failed to load DGU consumable stock.')); }
    finally { setLoading(false); }
  }, [page, pageSize, search, kindFilter]);
  useEffect(() => { load(); }, [load]);

  const onSearch = useCallback((val) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { setPage(1); setSearch(val); }, 300);
  }, []);

  const columns = useMemo(() => ([
    { title: 'Consumable item', dataIndex: 'itemName', ellipsis: true,
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { title: 'Kind', dataIndex: 'consumableKind', width: 130, render: (v) => <Tag color={kindColor(v)}>{kindLabel(v)}</Tag> },
    { title: 'Balance', dataIndex: 'qtyBalance', width: 130, align: 'right',
      render: (v) => <span className="tabular-nums" style={{ fontWeight: 700, color: '#0f4fb0' }}>{fmtQty(v)}</span> },
    { title: 'Unit', dataIndex: 'stockUom', width: 70 },
    { title: 'Last movement', dataIndex: 'lastMovementAt', width: 130, render: (v) => fmtDate(v) }
  ]), []);

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <Space>
          <Input.Search allowClear placeholder="Search item" style={{ width: 220 }} onChange={(e) => onSearch(e.target.value)} />
          <Select allowClear placeholder="All kinds" style={{ width: 150 }} value={kindFilter}
            onChange={(v) => { setKindFilter(v || undefined); setPage(1); }} options={KIND_OPTIONS} />
        </Space>
        <Space>
          {canAdjust ? <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdjustOpen(true)}>Add / adjust stock</Button> : null}
          <Button icon={<ReloadOutlined />} onClick={load} />
        </Space>
      </div>
      <Table rowKey="itemId" size="small" loading={loading} columns={columns} dataSource={rows} sticky scroll={{ x: 'max-content' }}
        pagination={{ current: page, pageSize, total, showSizeChanger: true, size: 'small', pageSizeOptions: ['25', '50', '100'],
          onChange: (p, ps) => { if (ps !== pageSize) { setPageSize(ps); setPage(1); } else { setPage(p); } },
          showTotal: (t) => `${t} consumable${t === 1 ? '' : 's'} in stock` }}
        locale={{ emptyText: loading ? ' ' : 'No DGU consumable stock yet — receive through Purchase (PR → PO → GRN → Bill → Post), or use “Add / adjust stock” for an opening balance.' }} />
      <AdjustStockModal open={adjustOpen} onClose={() => setAdjustOpen(false)}
        onDone={() => { load(); if (onDataChange) onDataChange(); }} />
    </>
  );
}

function LedgerTab({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [direction, setDirection] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dguConsumableStockApi.ledger({ page, pageSize, direction: direction || undefined });
      setRows((res && res.rows) || []); setTotal((res && res.total) || 0);
    } catch (e) { message.error(errMsg(e, 'Failed to load DGU ledger.')); }
    finally { setLoading(false); }
  }, [page, pageSize, direction, refreshKey]);
  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => ([
    { title: 'Date', dataIndex: 'movementDate', width: 120, render: (v) => fmtDate(v) },
    { title: 'Consumable item', dataIndex: 'itemName', ellipsis: true },
    { title: 'Direction', dataIndex: 'direction', width: 100,
      render: (v) => <Tag color={v === 'INWARD' ? 'green' : v === 'OUTWARD' ? 'red' : 'default'}>{v}</Tag> },
    { title: 'Source', key: 'src', width: 150,
      render: (_, r) => (r.sourceNo ? <span>{r.sourceType} · {r.sourceNo}</span> : <Text type="secondary">{r.sourceType || '—'}</Text>) },
    { title: 'Change', dataIndex: 'movementQty', width: 120, align: 'right',
      render: (v) => {
        const n = Number(v);
        const color = Number.isFinite(n) ? (n < 0 ? '#cf1322' : (n > 0 ? '#237804' : undefined)) : undefined;
        return <span className="tabular-nums" style={{ color }}>{fmtSigned(v)}</span>;
      } },
    { title: 'Unit', dataIndex: 'stockUom', width: 64 },
    { title: 'Balance', dataIndex: 'qtyBalanceAfter', width: 120, align: 'right',
      render: (v) => <span className="tabular-nums">{v != null ? fmtQty(v) : '—'}</span> }
  ]), []);

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <Select allowClear placeholder="All movements" style={{ width: 160 }} value={direction}
          onChange={(v) => { setDirection(v || undefined); setPage(1); }}
          options={[{ value: 'INWARD', label: 'Inward' }, { value: 'OUTWARD', label: 'Outward' }, { value: 'ADJUSTMENT', label: 'Adjustment' }]} />
        <Button icon={<ReloadOutlined />} onClick={load} />
      </div>
      <Table rowKey="id" size="small" loading={loading} columns={columns} dataSource={rows} sticky scroll={{ x: 'max-content' }}
        pagination={{ current: page, pageSize, total, showSizeChanger: true, size: 'small', pageSizeOptions: ['25', '50', '100'],
          onChange: (p, ps) => { if (ps !== pageSize) { setPageSize(ps); setPage(1); } else { setPage(p); } },
          showTotal: (t) => `${t} movement${t === 1 ? '' : 's'}` }}
        locale={{ emptyText: loading ? ' ' : 'No DGU consumable movements yet.' }} />
    </>
  );
}

export default function DguConsumableInventoryPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div className="p-4">
      <div className="mb-3">
        <Title level={4} style={{ margin: 0 }}><BorderOuterOutlined style={{ marginRight: 8 }} />DGU Consumables Inventory</Title>
        <Text type="secondary">Separate spacer (m) / desiccant &amp; sealant (kg) / corner-key (pcs) stock, received through Purchase. Kept apart from glass inventory.</Text>
      </div>
      <Alert type="info" showIcon className="mb-3"
        message="Separate from glass stock"
        description="DGU consumable purchases (GRN/Bill) post here in each item's own unit, never into the glass Current Stock." />
      <Card size="small" bodyStyle={{ paddingTop: 8 }}>
        <Tabs defaultActiveKey="balances" items={[
          { key: 'balances', label: 'Balances', children: <BalancesTab onDataChange={() => setRefreshKey((k) => k + 1)} /> },
          { key: 'ledger', label: 'Ledger', children: <LedgerTab refreshKey={refreshKey} /> }
        ]} />
      </Card>
    </div>
  );
}
